import { Button, DialogContent, Dialogß } from '@otter-ai/den-components';
import { CloseIcon } from '@otter-ai/den-icons';
import { FILE_STATES } from '@rpldy/uploady';
import { useRef, useState } from 'react';
import { SectionalErrorBoundary } from '@/shared/components/SectionalErrorBoundary';
import { useModalKey } from '@/shared/modals';
import type { UploadItem, UploadModalContextData } from '../types/types';
import { UploadRoot } from '../UploadRoot';
import { invalidateSubscriptionData } from '../utils/utils';
import { UploadCloseConfirmationDialog } from './UploadCloseConfirmationDialog';

const MODAL_KEY = 'upload-file-modal';

export function UploadFilesModal() {
  const [showCloseConfirmDialog, setShowCloseConfirmDialog] = useState(false);
  const uploadItemsRef = useRef<UploadItem[]>([]);
  const abortFunctionRef = useRef<((items: UploadItem[]) => void) | null>(null);
  const { isActive, hideModal, modalData } = useModalKey(MODAL_KEY);

  const contextData: UploadModalContextData =
    modalData as UploadModalContextData;

  function handleModalClose() {
    const uploadingItems = uploadItemsRef.current;

    const hasActiveUploads = uploadingItems?.some(
      (item) =>
        item.state === FILE_STATES.UPLOADING ||
        item.state === FILE_STATES.PENDING ||
        item.state === FILE_STATES.ADDED,
    );

    // If there are active uploads, show the closing dialog
    if (hasActiveUploads) {
      setShowCloseConfirmDialog(true);
      return;
    }

    invalidateSubscriptionData();
    hideModal();
  }

  function handleConfirmClose() {
    setShowCloseConfirmDialog(false);
    // Use the abort function passed from inside the Uploady context
    if (abortFunctionRef.current) {
      abortFunctionRef.current(uploadItemsRef.current);
    }

    invalidateSubscriptionData();
    hideModal();
  }

  const updateUploadItems = (items: UploadItem[]) => {
    uploadItemsRef.current = items;
  };

  const setAbortFunction = (abortFn: (items: UploadItem[]) => void) => {
    abortFunctionRef.current = abortFn;
  };

  return (
    <Dialogß
      data-testid="upload-files-modal"
      className="h-[100vh] md:h-[70vh] text-center rounded-lg duration-0"
      isOpen={isActive}
      onOpenChange={handleModalClose}
      title="Transcribe audio and video"
      size="lg"
      action={
        <Button
          className="top-1 right-0 md:top-6 md:right-6 absolute font-semibold"
          variant="ghost"
          onClick={handleModalClose}
          size="lg"
        >
          <CloseIcon />
        </Button>
      }
    >
      <DialogContent className="px-0 pt-0 pb-3">
        <SectionalErrorBoundary loggerName="import">
          <UploadRoot
            updateUploadItems={updateUploadItems}
            setAbortFunction={setAbortFunction}
            contextData={contextData}
          />
        </SectionalErrorBoundary>
        {showCloseConfirmDialog && (
          <UploadCloseConfirmationDialog
            onConfirmClose={handleConfirmClose}
            setShowCloseConfirmDailog={setShowCloseConfirmDialog}
          />
        )}
      </DialogContent>
    </Dialogß>
  );
}
